/**
 * The composer form as plain data, and its conversion to and from a POST /v1/messages body.
 * Kept free of React so the rules are easy to follow.
 */
import type { ChannelItem, MessageDetail, MessagePriority, SendMessageInput } from "@aiyo/shared";

export type AudienceKind = "all" | "customers" | "addresses";
export const COMPOSE_CHANNELS = ["fcm", "email", "sms", "whatsapp"] as const;
export type ComposeChannel = (typeof COMPOSE_CHANNELS)[number];

export interface Draft {
  audience: AudienceKind;
  customerIds: string[];
  emails: string;
  phones: string;
  tokens: string;
  channels: Record<ComposeChannel, boolean>;
  /** Credential alias per channel; empty means the app default. */
  credentials: Partial<Record<ComposeChannel, string>>;
  fcm: { title: string; body: string; image: string; link: string; data: string };
  email: {
    subject: string;
    format: "html" | "text";
    html: string;
    text: string;
    fromName: string;
    replyTo: string;
  };
  sms: { text: string };
  whatsapp: { template: string; language: string; variables: string };
  priority: MessagePriority;
  category: string;
  /** False until the user edits the category, so it can follow the audience (broadcasts are marketing). */
  categoryTouched: boolean;
}

export function emptyDraft(): Draft {
  return {
    audience: "customers",
    customerIds: [],
    emails: "",
    phones: "",
    tokens: "",
    channels: { fcm: true, email: false, sms: false, whatsapp: false },
    credentials: {},
    fcm: { title: "", body: "", image: "", link: "", data: "" },
    email: { subject: "", format: "html", html: "", text: "", fromName: "", replyTo: "" },
    sms: { text: "" },
    whatsapp: { template: "", language: "id", variables: "" },
    priority: "normal",
    category: "transactional",
    categoryTouched: false,
  };
}

export const splitList = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const lines = (text: string) =>
  text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

/** `key=value` per line. Returns the lines that have no `=`, so the form can point at them. */
export function parseData(text: string): { data: Record<string, string>; bad: string[] } {
  const data: Record<string, string> = {};
  const bad: string[] = [];
  for (const line of lines(text)) {
    const at = line.indexOf("=");
    if (at <= 0) bad.push(line);
    else data[line.slice(0, at).trim()] = line.slice(at + 1).trim();
  }
  return { data, bad };
}

/** Only the fields that have a value, so the API sees the same body a developer would write. */
function compact<T extends Record<string, unknown>>(o: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== "" && v !== undefined && v !== null),
  ) as Partial<T>;
}

export function channelItem(d: Draft, channel: ComposeChannel): ChannelItem | null {
  const credential = d.credentials[channel] || undefined;
  switch (channel) {
    case "fcm": {
      const { data } = parseData(d.fcm.data);
      return {
        type: "fcm",
        ...compact({ credential }),
        payload: {
          ...compact({
            title: d.fcm.title.trim(),
            body: d.fcm.body.trim(),
            image: d.fcm.image.trim(),
            link: d.fcm.link.trim(),
          }),
          ...(Object.keys(data).length ? { data } : {}),
        },
      } as ChannelItem;
    }
    case "email":
      return {
        type: "email",
        ...compact({ credential }),
        payload: {
          subject: d.email.subject.trim(),
          ...(d.email.format === "html"
            ? compact({ html: d.email.html, text: d.email.text })
            : compact({ text: d.email.text })),
          ...compact({ from_name: d.email.fromName.trim(), reply_to: d.email.replyTo.trim() }),
        },
      } as ChannelItem;
    case "sms":
      return { type: "sms", ...compact({ credential }), payload: { text: d.sms.text } } as ChannelItem;
    case "whatsapp": {
      const variables = lines(d.whatsapp.variables);
      return {
        type: "whatsapp",
        ...compact({ credential }),
        payload: {
          template: d.whatsapp.template.trim(),
          language: d.whatsapp.language.trim() || "id",
          ...(variables.length ? { variables } : {}),
        },
      } as ChannelItem;
    }
  }
}

export const enabledChannels = (d: Draft) => COMPOSE_CHANNELS.filter((c) => d.channels[c]);

export function buildRequest(d: Draft): SendMessageInput {
  const channels = enabledChannels(d);
  const to: SendMessageInput["to"] =
    d.audience === "all"
      ? { all_customers: true }
      : d.audience === "customers"
        ? { customer_ids: d.customerIds }
        : {
            addresses: {
              ...(d.channels.email && splitList(d.emails).length ? { emails: splitList(d.emails) } : {}),
              ...((d.channels.sms || d.channels.whatsapp) && splitList(d.phones).length
                ? { phones: splitList(d.phones) }
                : {}),
              ...(d.channels.fcm && splitList(d.tokens).length
                ? { push_tokens: splitList(d.tokens).map((token) => ({ channel: "fcm" as const, token })) }
                : {}),
            },
          };
  return {
    to,
    channels: channels.map((c) => channelItem(d, c)!),
    priority: d.priority,
    ...(d.category.trim() ? { category: d.category.trim() } : {}),
  };
}

/** Field problems the form can show before asking the API. Keys are field names, values i18n keys. */
export function validate(d: Draft): Record<string, string> {
  const problems: Record<string, string> = {};
  const channels = enabledChannels(d);
  if (channels.length === 0) problems.channels = "send.errors.channel";
  if (d.audience === "customers" && d.customerIds.length === 0) problems.customers = "send.errors.customers";
  if (d.audience === "addresses") {
    const req = buildRequest(d);
    const a = req.to.addresses ?? {};
    if (!a.emails && !a.phones && !a.push_tokens) problems.addresses = "send.errors.addresses";
  }
  if (d.audience === "all" && !d.category.trim()) problems.category = "send.errors.category";
  if (d.channels.fcm) {
    const { data, bad } = parseData(d.fcm.data);
    if (bad.length) problems["fcm.data"] = "send.errors.data";
    if (!d.fcm.title.trim() && !d.fcm.body.trim() && Object.keys(data).length === 0) {
      problems["fcm.title"] = "send.errors.pushContent";
    }
    if (d.fcm.link.trim() && !/^https:\/\//i.test(d.fcm.link.trim()))
      problems["fcm.link"] = "send.errors.https";
  }
  if (d.channels.email) {
    if (!d.email.subject.trim()) problems["email.subject"] = "send.errors.subject";
    const body = d.email.format === "html" ? d.email.html : d.email.text;
    if (!body.trim()) problems[`email.${d.email.format}`] = "send.errors.emailBody";
  }
  if (d.channels.sms && !d.sms.text.trim()) problems["sms.text"] = "send.errors.smsText";
  if (d.channels.whatsapp && !d.whatsapp.template.trim())
    problems["whatsapp.template"] = "send.errors.template";
  return problems;
}

/** Start from the content of an earlier message ("Duplicate"). Recipients are chosen again on purpose. */
export function fromMessage(m: MessageDetail, base: Draft): Draft {
  const d: Draft = {
    ...base,
    channels: { fcm: false, email: false, sms: false, whatsapp: false },
    credentials: {},
    priority: m.priority,
    category: m.category,
    categoryTouched: true,
    audience: m.audience.all_customers ? "all" : base.audience,
  };
  for (const item of m.content) {
    const p = item.payload as Record<string, unknown>;
    const s = (k: string) => (typeof p[k] === "string" ? (p[k] as string) : "");
    if (item.credential && (COMPOSE_CHANNELS as readonly string[]).includes(item.type)) {
      d.credentials[item.type as ComposeChannel] = item.credential;
    }
    switch (item.type) {
      case "fcm": {
        const data = (p.data ?? {}) as Record<string, string>;
        d.channels.fcm = true;
        d.fcm = {
          title: s("title"),
          body: s("body"),
          image: s("image"),
          link: s("link"),
          data: Object.entries(data)
            .map(([k, v]) => `${k}=${v}`)
            .join("\n"),
        };
        break;
      }
      case "email":
        d.channels.email = true;
        d.email = {
          subject: s("subject"),
          format: s("html") ? "html" : "text",
          html: s("html"),
          text: s("text"),
          fromName: s("from_name"),
          replyTo: s("reply_to"),
        };
        break;
      case "sms":
        d.channels.sms = true;
        d.sms = { text: s("text") };
        break;
      case "whatsapp":
        d.channels.whatsapp = true;
        d.whatsapp = {
          template: s("template"),
          language: s("language") || "id",
          variables: Array.isArray(p.variables) ? (p.variables as string[]).join("\n") : "",
        };
        break;
    }
  }
  return d;
}
